import { useEffect, useState } from "react";
import { BrainCircuit, Eye, LineChart, Sparkles, Users } from "lucide-react";
import type { ReactNode } from "react";

import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

interface ClosurePrediction {
  title: string;
  status_label: string;
  asking_price: number;
  views: number;
  interests: number;
  score: number;
}

interface BuyerMatch {
  name: string;
  initials: string;
  description: string;
  fit: number;
}

interface MostViewed {
  title: string;
  views: number;
  watchers: number;
  avatars: string[];
}

interface CommissionTracker {
  earned: number;
  closed_count: number;
  if_all_close: number;
  ai_predicted: number;
  avg_per_deal: number;
  rate: number;
}

interface Insights {
  closure_predictions: ClosurePrediction[];
  buyer_matches: BuyerMatch[];
  most_viewed: MostViewed[];
  commission_by_stage: { nda: number; loi: number; offer: number; closed: number };
  commission_tracker: CommissionTracker;
}

const ALGO_SIGNALS = ["Stage velocity", "NDA → LOI rate", "Buyer engagement", "Days in stage", "Competing offers", "Price alignment"];

const AVATAR_TONES = [
  "bg-primary/15 text-primary",
  "bg-emerald-500/15 text-emerald-600",
  "bg-amber-500/15 text-amber-600",
  "bg-primary/25 text-primary",
  "bg-rose-500/15 text-rose-600",
  "bg-emerald-500/25 text-emerald-600",
];

const trim = (n: number) => n.toFixed(2).replace(/\.?0+$/, "");
function fmtMoney(v: number): string {
  if (!v) return "$0";
  if (v >= 1e9) return `$${trim(v / 1e9)}B`;
  if (v >= 1e6) return `$${trim(v / 1e6)}M`;
  if (v >= 1e3) return `$${Math.round(v / 1e3)}K`;
  return `$${Math.round(v)}`;
}
const fmtFull = (v: number) => `$${Math.round(v).toLocaleString()}`;

function scoreColor(s: number) {
  if (s >= 75) return "#10b981";
  if (s >= 55) return "#d97706";
  return "#ef4444";
}

function Panel({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn("rounded-3xl border border-border/70 bg-card p-5 shadow-sm sm:p-6", className)}>{children}</div>;
}

function PanelHead({ icon: Icon, title, subtitle, badge }: { icon: typeof Eye; title: string; subtitle: string; badge?: ReactNode }) {
  return (
    <div className="pb-4">
      <div className="flex items-center gap-2">
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Icon className="h-4 w-4" />
        </span>
        <h3 className="text-base font-bold tracking-tight text-foreground">{title}</h3>
        {badge}
      </div>
      <p className="mt-1.5 text-xs leading-5 text-muted-foreground">{subtitle}</p>
    </div>
  );
}

export default function BLAdvisorInsights() {
  const [data, setData] = useState<Insights | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get<Insights>("/dashboard/bl-advisor/insights")
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading || !data) return null;

  /* Gold funnel ramp (light → deep brand gold), closed = success green. Literal
     hexes: these feed `${color}bb` alpha concatenation, where var() won't work. */
  const stages = [
    { key: "nda" as const, label: "NDA Stage", color: "#e6cd86" },
    { key: "loi" as const, label: "LOI Stage", color: "#d8b45c" },
    { key: "offer" as const, label: "Offer Stage", color: "#a9842b" },
    { key: "closed" as const, label: "Closed", color: "#33a86a" },
  ];
  const stageVals = stages.map((s) => data.commission_by_stage[s.key]);
  const maxComm = Math.max(...stageVals, 1);
  const ticks = 5;
  const topComm = Math.ceil(maxComm / ticks) * ticks || ticks;
  const tickVals = Array.from({ length: ticks + 1 }, (_, i) => topComm - (topComm / ticks) * i);

  return (
    <div className="grid gap-5 lg:grid-cols-2">
      {/* ── AI Closure Predictions ─────────────────────────────────── */}
      <Panel>
        <PanelHead
          icon={BrainCircuit}
          title="AI Closure Predictions"
          subtitle="Top 5 deals ranked by closure probability score"
          badge={
            <span className="inline-flex items-center gap-1 rounded-full bg-primary px-2 py-0.5 text-[0.62rem] font-bold uppercase tracking-wide text-primary-foreground">
              <Sparkles className="h-3 w-3" />
              AI Insight
            </span>
          }
        />
        {data.closure_predictions.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">No open deals to score yet.</p>
        ) : (
          <div className="space-y-2.5">
            {data.closure_predictions.map((d, i) => (
              <div key={i} className="flex items-center gap-3 rounded-2xl border border-border/60 bg-muted/20 p-3">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
                  {i + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-bold text-foreground">{d.title}</div>
                  <div className="truncate text-[0.72rem] text-muted-foreground">
                    {d.status_label} · {fmtMoney(d.asking_price)} · {d.views} views · {d.interests} interested
                  </div>
                </div>
                <div className="w-24 shrink-0">
                  <div className="text-right text-sm font-extrabold" style={{ color: scoreColor(d.score) }}>{d.score}%</div>
                  <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                    <div className="h-full rounded-full" style={{ width: `${d.score}%`, backgroundColor: scoreColor(d.score) }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="mt-4 border-t border-border/60 pt-3">
          <div className="mb-2 text-[0.72rem] font-semibold text-muted-foreground">Algorithm signals</div>
          <div className="flex flex-wrap gap-1.5">
            {ALGO_SIGNALS.map((s) => (
              <span key={s} className="rounded-full border border-border/70 bg-muted/30 px-2.5 py-1 text-[0.68rem] font-medium text-muted-foreground">
                {s}
              </span>
            ))}
          </div>
        </div>
      </Panel>

      {/* ── Best Buyer Matches ─────────────────────────────────────── */}
      <Panel>
        <PanelHead icon={Users} title="Best Buyer Matches for Your Deals" subtitle="Matched by industry, budget & acquisition history" />
        {data.buyer_matches.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">No buyer profiles to match yet.</p>
        ) : (
          <div className="space-y-2.5">
            {data.buyer_matches.map((b, i) => (
              <div key={i} className="flex items-center gap-3 rounded-2xl border border-border/60 bg-muted/20 p-3">
                <span className={cn("flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-bold", AVATAR_TONES[i % AVATAR_TONES.length])}>
                  {b.initials}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-bold text-foreground">{b.name}</div>
                  <div className="truncate text-[0.72rem] text-muted-foreground">{b.description}</div>
                </div>
                <span className="shrink-0 rounded-full bg-primary/10 px-2.5 py-1 text-[0.7rem] font-bold text-primary">{b.fit}% fit</span>
              </div>
            ))}
          </div>
        )}
      </Panel>

      {/* ── Most Viewed Listings ───────────────────────────────────── */}
      <Panel>
        <PanelHead icon={Eye} title="Most Viewed Listings & Who Watched" subtitle="Your listings ranked by unique viewers" />
        {data.most_viewed.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">No views recorded yet.</p>
        ) : (
          <div className="space-y-2.5">
            {data.most_viewed.map((m, i) => (
              <div key={i} className="flex items-center gap-3 rounded-2xl border border-border/60 bg-muted/20 p-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Eye className="h-4 w-4" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-bold text-foreground">{m.title}</div>
                  <div className="text-[0.72rem] text-muted-foreground">
                    {m.views} {m.views === 1 ? "view" : "views"} · {m.watchers} {m.watchers === 1 ? "watcher" : "watchers"}
                  </div>
                </div>
                <div className="flex shrink-0 -space-x-2">
                  {m.avatars.map((a, j) => (
                    <span
                      key={j}
                      className={cn("flex h-7 w-7 items-center justify-center rounded-full border-2 border-card text-[0.62rem] font-bold", AVATAR_TONES[(i + j) % AVATAR_TONES.length])}
                    >
                      {a}
                    </span>
                  ))}
                  {m.watchers > m.avatars.length && (
                    <span className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-card bg-muted text-[0.6rem] font-bold text-muted-foreground">
                      +{m.watchers - m.avatars.length}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>

      {/* ── Commission Projection by Stage ─────────────────────────── */}
      <Panel>
        <PanelHead icon={LineChart} title="Commission Projection by Stage" subtitle="Projected commission across the deal funnel" />
        <div className="flex gap-3">
          <div className="flex h-44 flex-col justify-between py-1 text-[10px] font-semibold text-muted-foreground">
            {tickVals.map((t, i) => (
              <span key={i} className="leading-none">{fmtMoney(t)}</span>
            ))}
          </div>
          <div className="relative flex-1">
            <div className="absolute inset-0 flex h-44 flex-col justify-between">
              {tickVals.map((_, i) => (
                <div key={i} className="border-t border-dashed border-border/50" />
              ))}
            </div>
            <div className="relative flex h-44 items-end justify-around gap-4">
              {stages.map((s) => {
                const v = data.commission_by_stage[s.key];
                return (
                  <div key={s.key} className="flex h-full flex-1 items-end justify-center">
                    <div
                      className="w-full max-w-[64px] rounded-t-lg shadow-sm transition-all duration-300"
                      style={{ height: `${(v / topComm) * 100}%`, minHeight: v > 0 ? 6 : 0, background: `linear-gradient(to top, ${s.color}, ${s.color}bb)` }}
                      title={`${s.label}: ${fmtFull(v)}`}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <div className="mt-2 flex gap-4 pl-12">
          {stages.map((s) => (
            <div key={s.key} className="flex-1 text-center text-[0.7rem] font-bold text-muted-foreground">{s.label}</div>
          ))}
        </div>

        <div className="mt-5 border-t border-border/60 pt-4">
          <div className="mb-3 text-[0.72rem] font-semibold text-muted-foreground">
            Commission Tracker ({trim(data.commission_tracker.rate)}% rate)
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-2xl border border-border/60 bg-muted/20 px-4 py-3">
              <div className="text-[0.7rem] text-muted-foreground">Earned ({data.commission_tracker.closed_count} closed)</div>
              <div className="mt-0.5 text-lg font-black tracking-tight text-foreground">{fmtFull(data.commission_tracker.earned)}</div>
            </div>
            <div className="rounded-2xl border border-border/60 bg-muted/20 px-4 py-3">
              <div className="text-[0.7rem] text-muted-foreground">If all close</div>
              <div className="mt-0.5 text-lg font-black tracking-tight text-primary">{fmtFull(data.commission_tracker.if_all_close)}</div>
            </div>
            <div className="rounded-2xl border border-emerald-500/25 bg-emerald-500/10 px-4 py-3">
              <div className="text-[0.7rem] text-muted-foreground">AI-predicted earn</div>
              <div className="mt-0.5 text-lg font-black tracking-tight text-emerald-600">{fmtFull(data.commission_tracker.ai_predicted)}</div>
            </div>
            <div className="rounded-2xl border border-border/60 bg-muted/20 px-4 py-3">
              <div className="text-[0.7rem] text-muted-foreground">Avg per deal</div>
              <div className="mt-0.5 text-lg font-black tracking-tight text-foreground">{fmtFull(data.commission_tracker.avg_per_deal)}</div>
            </div>
          </div>
        </div>
      </Panel>
    </div>
  );
}
